import { useState, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { uploadFile, deleteFile, getFileUrl } from '../lib/api';
import { formatDate } from '../lib/utils';
import {
    Image,
    File,
    FilePdf,
    Trash,
    Upload,
    Folder,
    X,
    Download,
    Eye
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';

export function FileSection({ productKey, files, onRefresh }) {
    const { isAdmin } = useAuth();
    const [uploading, setUploading] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const fileInputRefs = {
        image: useRef(null),
        document: useRef(null),
        packaging_photo: useRef(null),
    };

    const groupedFiles = {
        image: files.filter(f => f.media_type === 'image'),
        document: files.filter(f => f.media_type === 'document'),
        packaging_photo: files.filter(f => f.media_type === 'packaging_photo'),
        catalog_reference: files.filter(f => f.media_type === 'catalog_reference'),
        other: files.filter(f => !['image','document','packaging_photo','catalog_reference'].includes(f.media_type)),
    };

    const handleUpload = async (mediaType, event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            await uploadFile(productKey, mediaType, file);
            toast.success('File uploaded successfully');
            onRefresh();
        } catch (error) {
            toast.error('Failed to upload file');
        } finally {
            setUploading(false);
            event.target.value = '';
        }
    };

    const handleDelete = async (fileId) => {
        try {
            await deleteFile(fileId);
            toast.success('File deleted');
            onRefresh();
        } catch (error) {
            toast.error('Failed to delete file');
        }
    };

    const getFileIcon = (contentType) => {
        if (contentType?.startsWith('image/')) return Image;
        if (contentType?.includes('pdf')) return FilePdf;
        return File;
    };

    const isImage = (contentType) => contentType?.startsWith('image/');

    // Check if file has external URL or storage path
    const getFileDisplayUrl = (file) => {
        if (file.url) return file.url;
        if (file.file_url) return file.file_url;
        if (file.drive_link) return file.drive_link;
        if (file.storage_path && file.id) return getFileUrl(file.id);
        return null;
    };

    const FileGroup = ({ title, type, files: groupFiles, hideUpload = false }) => (
        <div className="bg-card border border-border rounded-lg">
            <div className="flex items-center justify-between p-4 border-b border-border">
                <div className="flex items-center gap-2">
                    <Folder size={18} className="text-muted-foreground" />
                    <h3 className="font-medium">{title}</h3>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                        {groupFiles.length}
                    </span>
                </div>
                {isAdmin() && !hideUpload && fileInputRefs[type] && (
                    <>
                        <input
                            type="file"
                            ref={fileInputRefs[type]}
                            className="hidden"
                            onChange={(e) => handleUpload(type, e)}
                            accept={type === 'image' || type === 'packaging_photo' ? 'image/*' : undefined}
                        />
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRefs[type].current?.click()}
                            disabled={uploading}
                            data-testid={`upload-${type}-btn`}
                        >
                            <Upload size={16} className="mr-2" />
                            Upload
                        </Button>
                    </>
                )}
            </div>
            <div className="p-4">
                {groupFiles.length === 0 ? (
                    <p className="text-muted-foreground text-sm text-center py-4">
                        No {title.toLowerCase()} uploaded yet
                    </p>
                ) : (
                    <div className={type === 'image' || type === 'packaging_photo' ? 'grid grid-cols-2 md:grid-cols-4 gap-4' : 'space-y-2'}>
                        {groupFiles.map((file) => {
                            const FileIcon = getFileIcon(file.content_type);
                            const fileUrl = getFileDisplayUrl(file);

                            if ((type === 'image' || type === 'packaging_photo') && (isImage(file.content_type) || file.file_url)) {
                                return (
                                    <div 
                                        key={file.id} 
                                        className="relative group aspect-square bg-muted rounded-md overflow-hidden"
                                        data-testid={`file-${file.id}`}
                                    >
                                        <img
                                            src={fileUrl}
                                            alt={file.alt_text || file.original_filename}
                                            className="w-full h-full object-contain"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f0f0f0" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23999">No Image</text></svg>';
                                            }}
                                        />
                                        {file.is_primary && (
                                            <span className="absolute top-2 left-2 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded">
                                                Primary
                                            </span>
                                        )}
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                            <Button
                                                size="icon"
                                                variant="secondary"
                                                className="h-8 w-8"
                                                onClick={() => setPreviewFile({...file, url: fileUrl})}
                                            >
                                                <Eye size={16} />
                                            </Button>
                                            {isAdmin() && (
                                                <Button
                                                    size="icon"
                                                    variant="destructive"
                                                    className="h-8 w-8"
                                                    onClick={() => handleDelete(file.id)}
                                                >
                                                    <Trash size={16} />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div 
                                    key={file.id}
                                    className="flex items-center gap-3 p-3 rounded-md border border-border hover:bg-muted/50"
                                    data-testid={`file-${file.id}`}
                                >
                                    <FileIcon size={24} className="text-muted-foreground flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">
                                            {file.original_filename || file.file_name || 'Document'}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {file.file_path && <span className="mr-2">{file.file_path}</span>}
                                            {formatDate(file.uploaded_at)}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        {fileUrl && (
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                className="h-8 w-8"
                                                asChild
                                            >
                                                <a href={fileUrl} download={file.original_filename} target="_blank" rel="noopener noreferrer">
                                                    <Download size={16} />
                                                </a>
                                            </Button>
                                        )}
                                        {isAdmin() && (
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                className="h-8 w-8 text-destructive hover:text-destructive"
                                                onClick={() => handleDelete(file.id)}
                                            >
                                                <Trash size={16} />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            <FileGroup title="Product Images" type="image" files={groupedFiles.image} />
            <FileGroup title="Documents" type="document" files={groupedFiles.document} />
            <FileGroup title="Packaging Photos" type="packaging_photo" files={groupedFiles.packaging_photo} />
            {groupedFiles.catalog_reference.length > 0 && (
                <FileGroup title="Catalog References" type="catalog_reference" files={groupedFiles.catalog_reference} hideUpload />
            )}
            {groupedFiles.other.length > 0 && (
                <FileGroup title="Other Media" type="other" files={groupedFiles.other} hideUpload />
            )}

            {/* Preview Dialog */}
            <Dialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>{previewFile?.original_filename || previewFile?.alt_text || 'Image'}</DialogTitle>
                    </DialogHeader>
                    {previewFile && (
                        <img
                            src={previewFile.url}
                            alt={previewFile.alt_text || previewFile.original_filename}
                            className="w-full max-h-[70vh] object-contain"
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
